import React, { useState, useEffect } from 'react';
import { networkUserAPI } from '../lib/api';

const CreateNetworkUserModal = ({
	isOpen,
	onClose,
	onSuccess,
	networkId,
	isEdit = false,
	initialData = null,
	useMyNetworkEndpoint = false,
	useMyNetworkApi = false
}) => {
	const useMyNetwork = useMyNetworkApi || useMyNetworkEndpoint;
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        jobTitle: '',
        department: '',
        networkRole: 'MANAGEMENT',
        canViewFiles: true,
        canViewInvestments: true,
        canManageUsers: false,
        canManageFiles: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (isEdit && initialData) {
                setFormData({
                    name: initialData.name || '',
                    email: initialData.email || '',
                    password: '',
                    phoneNumber: initialData.phoneNumber || '',
                    jobTitle: initialData.jobTitle || '',
                    department: initialData.department || '',
                    networkRole: initialData.networkRole || 'MANAGEMENT',
                    canViewFiles: initialData.canViewFiles !== undefined ? initialData.canViewFiles : true,
                    canViewInvestments: initialData.canViewInvestments !== undefined ? initialData.canViewInvestments : true,
                    canManageUsers: initialData.canManageUsers !== undefined ? initialData.canManageUsers : false,
                    canManageFiles: initialData.canManageFiles !== undefined ? initialData.canManageFiles : false
                });
            } else {
                setFormData({
                    name: '',
                    email: '',
                    password: '',
                    phoneNumber: '',
                    jobTitle: '',
                    department: '',
                    networkRole: 'MANAGEMENT',
                    canViewFiles: true,
                    canViewInvestments: true,
                    canManageUsers: false,
                    canManageFiles: false
                });
            }
            setError('');
        }
    }, [isOpen, isEdit, initialData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (!formData.name.trim()) {
                throw new Error('Name is required');
            }
            if (!formData.email.trim()) {
                throw new Error('Email is required');
            }
            if (!isEdit && !formData.password.trim()) {
                throw new Error('Password is required for new users');
            }
            if (!isEdit && formData.password.length < 6) {
                throw new Error('Password must be at least 6 characters');
            }

            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phoneNumber: formData.phoneNumber.trim() || null,
                jobTitle: formData.jobTitle.trim() || null,
                department: formData.department.trim() || null,
                networkRole: formData.networkRole,
                role: 'MANAGEMENT',
                canViewFiles: formData.canViewFiles,
                canViewInvestments: formData.canViewInvestments,
                canManageUsers: formData.canManageUsers,
                canManageFiles: formData.canManageFiles
            };

            if (!isEdit) {
                payload.password = formData.password;
            }

            if (isEdit) {
                if (useMyNetwork) {
                    await networkUserAPI.updateMyNetworkUser(initialData.id, payload);
                } else {
                    await networkUserAPI.updateNetworkUser(networkId, initialData.id, payload);
                }
            } else if (useMyNetwork) {
                await networkUserAPI.addMyNetworkUser(payload);
            } else {
                await networkUserAPI.addNetworkUser(networkId, payload);
            }

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('Error saving network user:', err);
            setError(err.message || 'Failed to save user');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phoneNumber: '',
            jobTitle: '',
            department: '',
            networkRole: 'MANAGEMENT',
            canViewFiles: true,
            canViewInvestments: true,
            canManageUsers: false,
            canManageFiles: false
        });
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header - same as Add Network User in admin */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white">
                            {isEdit ? 'Edit Network User' : 'Add Network User'}
                        </h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-white hover:text-gray-200 transition"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <p className="text-blue-100 text-sm mt-1">
                        {isEdit ? 'Update management user details' : 'Add a management user to this network with view and access permissions'}
                    </p>
                </div>

                {/* Form - same fields as admin Add Network User */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Basic Information */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="John Doe"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                                    placeholder="john@example.com"
                                    required
                                    disabled={isEdit}
                                />
                            </div>
                        </div>

                        {!isEdit && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Minimum 6 characters"
                                    minLength={6}
                                    required={!isEdit}
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phoneNumber}
                                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+91 98765 43210"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Job Title
                                </label>
                                <input
                                    type="text"
                                    value={formData.jobTitle}
                                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Investment Analyst"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Network Role
                            </label>
                            <select
                                value={formData.networkRole || 'MANAGEMENT'}
                                onChange={(e) => setFormData({ ...formData, networkRole: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            >
                                <option value="MANAGEMENT">Management</option>
                                <option value="ADMIN">Admin</option>
                                <option value="CEO">CEO</option>
                                <option value="ACCOUNTANT">Accountant</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Admin role can manage users and files within the network.</p>
                        </div>
                    </div>

                    {/* Permissions */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900">Permissions</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                <input
                                    type="checkbox"
                                    checked={formData.canViewFiles}
                                    onChange={(e) => setFormData({ ...formData, canViewFiles: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm text-gray-700">View Files</span>
                            </label>
                            <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                <input
                                    type="checkbox"
                                    checked={formData.canViewInvestments}
                                    onChange={(e) => setFormData({ ...formData, canViewInvestments: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm text-gray-700">View Investments</span>
                            </label>
                            <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                <input
                                    type="checkbox"
                                    checked={formData.canManageUsers}
                                    onChange={(e) => setFormData({ ...formData, canManageUsers: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm text-gray-700 font-medium">Can Manage Users</span>
                            </label>
                            <label className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                <input
                                    type="checkbox"
                                    checked={formData.canManageFiles}
                                    onChange={(e) => setFormData({ ...formData, canManageFiles: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm text-gray-700 font-medium">Can Manage Files</span>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={loading}
                        >
                            {loading && (
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {isEdit ? 'Update User' : 'Add User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateNetworkUserModal;
